import { useParams } from 'react-router-dom';

import { Breadcrumb } from '@components/breadcrumb';
import { Button } from '@components/button';
import { Card } from '@components/card';
import { EmptyState } from '@components/empty-state';
import { RouterTab, RouterTabsHeader } from '@components/header/router-tabs-header';
import { IconButton, IconButtonProps } from '@components/icon-button';
import { IconFancyContainer } from '@components/icon-fancy-container';
import { IconApi } from '@components/icons/api';
import { IconArrowLeft } from '@components/icons/arrow-left';
import { IconCloseRegular } from '@components/icons/close-regular';
import { IconDropdown } from '@components/icons/dropdown';
import { IconEndpoint } from '@components/icons/endpoint';
import { IconJourneyItem } from '@components/icons/journey-icon';
import { IconSettings } from '@components/icons/settings';
import { IconSSO } from '@components/icons/sso';
import { IconWebhook } from '@components/icons/webhook';
import { Link } from '@components/link';
import { ListCard } from '@components/list-card';
import { Spinner } from '@components/loading-spinner';
import { Menu } from '@components/menu';
import { MenuItem } from '@components/menu/menu';
import { PageContent } from '@components/page-containers/page-container';
import { MiddleColumnPage } from '@components/page-containers/page-containers';
import { PremiumTag } from '@components/premium-tag';
import { RadioGroup } from '@components/radio-group';
import { Stack } from '@components/stack';
import { StepWizard } from '@components/step-wizard';
import { Text } from '@components/text';
import { showSuccess } from '@components/toaster';
import { useScopedTranslate } from '@locale/translate';

import { useUserOption } from 'api/authentication/hooks';
import { useEditIntegration, useIntegration, useIntegrationJourneys } from 'api/integrations/hooks';
import { Integration } from 'api/integrations/requests';
import { JourneyListItem } from 'ui/authenticated/main/pages/journeys/journey-item';
import { routePaths } from 'ui/authenticated/navigation';
import { useFeature } from 'ui/hooks';

import { EndpointCard } from './endpoint';
import { useIntegrationEndpointCreateSidebar } from './sidebars/add-edit-endpoint';
import { useIntegrationWebhookCreateSidebar } from './sidebars/add-edit-webhook';
import {
  IntegrationProps,
  useAuthenticationSidebar,
  useAuthTypeLabel,
} from './sidebars/authentication';
import { WebhookCard } from './webhook';

export const IntegrationDetail = () => {
  const { t } = useScopedTranslate(locale);
  const { integrationId } = useParams<{ integrationId: string }>();

  const integrationQuery = useIntegration(integrationId!);

  if (!integrationQuery.data) {
    return <Spinner />;
  }
  const integration = integrationQuery.data;

  const tabs: RouterTab[] = [
    {
      content: <Details integration={integration} />,
      label: t('details'),
      section: 'details',
    },
    {
      content: <JourneysList integration={integration} />,
      counter: integration.numJourneys,
      label: t('journeys'),
      section: 'journeys',
    },
    {
      content: <IntegrationSettings integration={integration} />,
      label: t('settings'),
      section: 'settings',
      icon: <IconSettings />,
    },
  ];

  return (
    <RouterTabsHeader
      backAction={<BackAction />}
      actions={<HeaderActions integration={integration} />}
      breadcrumbs={<Breadcrumb items={[{ label: integration.name }]} />}
      tabs={tabs}
    />
  );
};

const BackAction = ({
  icon = <IconArrowLeft />,
  variant = 'ghost-purple',
}: {
  icon?: JSX.Element;
  variant?: IconButtonProps['variant'];
}) => (
  <Link to={routePaths.integrations}>
    <IconButton data-cy="journey-back-btn" icon={icon} variant={variant} />
  </Link>
);

const HeaderActions = ({ integration }: IntegrationProps) => {
  const { t } = useScopedTranslate(locale);
  const showCreateEndpointSidebar = useIntegrationEndpointCreateSidebar();
  const showCreateWebhookSidebar = useIntegrationWebhookCreateSidebar();
  const { isEnabled: hasWebhooks } = useFeature('FEATURE_INTEGRATION_WEBHOOKS');
  return (
    <div>
      <Stack orientation="horizontal" space={2}>
        <Menu
          buttonProps={{
            text: t('addEndpoint'),
            iconLeft: <IconEndpoint />,
            iconRight: <IconDropdown />,
            variant: 'primary',
          }}
        >
          <MenuItem
            icon={<IconApi />}
            label={t('api')}
            onSelect={() => showCreateEndpointSidebar({ integrationId: integration.shortId })}
          />
          <MenuItem
            icon={<IconWebhook />}
            label={t('webhook')}
            extraIcon={
              hasWebhooks ? undefined : (
                <Stack align="center">
                  <PremiumTag />
                </Stack>
              )
            }
            extraIconFullOpacity
            onSelect={() => showCreateWebhookSidebar({ integrationId: integration.shortId })}
            isDisabled={!hasWebhooks}
          />
        </Menu>
        <BackAction icon={<IconCloseRegular />} variant="secondary-purple" />
      </Stack>
    </div>
  );
};

const Details = ({ integration }: IntegrationProps) => {
  const { t } = useScopedTranslate(locale);
  const showAuthenticationSidebar = useAuthenticationSidebar();
  const authTypeLabel = useAuthTypeLabel(integration.authentication?.authType);
  const hasAuthentication = Boolean(integration.authentication?.isAuthenticated);

  return (
    <PageContent>
      <Stack space={2}>
        <ListCard
          icon={
            <IconFancyContainer icon={<IconSSO />} variant={hasAuthentication ? 'green' : 'gray'} />
          }
          spaceX={4}
          spaceY={4}
          title={t('authentication')}
          description={authTypeLabel}
          actions={
            <Button
              variant={hasAuthentication ? 'ghost' : 'primary-blue'}
              text={hasAuthentication ? t('core.edit') : t('authenticate')}
              onClick={() => showAuthenticationSidebar({ integration })}
            />
          }
        />
        {integration.endpoints.map((endpoint) => (
          <EndpointCard endpoint={endpoint} key={endpoint.shortId} />
        ))}
        {integration.webhooks.map((webhook) => (
          <WebhookCard webhook={webhook} key={webhook.shortId} />
        ))}
        {integration.endpoints.length === 0 && (
          <Card variant="transparent" spaceX={6} spaceY={4}>
            <Stack orientation="horizontal" justify="space-between" align="center">
              <Stack orientation="horizontal" space={4} align="center">
                <IconEndpoint color="elementPurple60" />
                <Text font="semibold14" text={t('noEndpoint')} color="textSecondary" />
              </Stack>
            </Stack>
          </Card>
        )}
        <SetupHelper integration={integration} />
      </Stack>
    </PageContent>
  );
};

const SetupHelper = ({ integration }: { integration: Integration }) => {
  const { t } = useScopedTranslate(locale);

  const { value: hasHidden, update } = useUserOption('hasHiddenIntegrationWizard', false);
  if (hasHidden) {
    return null;
  }
  const integrationStep = !integration.authentication
    ? 'connect'
    : integration.endpoints.length === 0
    ? 'addEndpoint'
    : 'configure';
  const isFinished = integration.endpoints.some(
    (endpoint) => Object.keys(endpoint.responseValues ?? {}).length > 0
  );
  const steps = [
    {
      id: 'connect',
      label: t('connect'),
      description: t('connectDescription'),
    },
    {
      id: 'addEndpoint',
      label: t('addEndpoint'),
      description: t('addEndpointDescription'),
    },
    {
      id: 'configure',
      label: t('configure'),
      description: t('configureDescription'),
    },
  ] as const;

  return (
    <StepWizard
      title={t('finishSetup')}
      steps={steps}
      currentStep={integrationStep}
      onDismiss={() => update.mutate(true)}
      isFinished={isFinished}
    />
  );
};

const JourneysList = ({ integration }: IntegrationProps) => {
  const { t } = useScopedTranslate(locale);
  const journeys = useIntegrationJourneys(integration.shortId);

  if (!journeys.data) {
    return <Spinner />;
  }

  return (
    <PageContent>
      <Stack space={2}>
        {journeys.data.length === 0 ? (
          <EmptyState
            skipMargin
            icon={<IconJourneyItem size={80} />}
            header={t('noJourneys')}
            content={t('noJourneysDescription')}
          />
        ) : (
          <Stack space={2}>
            {journeys.data.map((journey) => (
              <JourneyListItem key={journey.shortId} journey={journey} hideMenu />
            ))}
          </Stack>
        )}
      </Stack>
    </PageContent>
  );
};

const IntegrationSettings = ({ integration }: IntegrationProps) => {
  const { t } = useScopedTranslate(locale);
  const updateIntegration = useEditIntegration(() => showSuccess(t('integrationSaved')));
  return (
    <PageContent>
      <MiddleColumnPage>
        <RadioGroup
          label={t('emptyFieldHandlingLabel')}
          description={t('emptyFieldHandlingDescription')}
          defaultValue={integration.emptyFieldHandling}
          onChange={(value) =>
            updateIntegration.mutate({
              integrationId: integration.shortId,
              data: { emptyFieldHandling: value as Integration['emptyFieldHandling'] },
            })
          }
        >
          <RadioGroup.Option
            key="empty_string"
            label={t('emptyFieldHandlingEmptyString')}
            value="empty_string"
          />
          <RadioGroup.Option key="null" label={t('emptyFieldHandlingNull')} value="null" />
          <RadioGroup.Option key="skip" label={t('emptyFieldHandlingSkip')} value="skip" />
        </RadioGroup>
      </MiddleColumnPage>
    </PageContent>
  );
};

const locale = {
  en: {
    name: 'Name',
    description: 'Description',
    details: 'Details',
    journeys: 'Journeys',
    settings: 'Settings',
    addEndpoint: 'Add Endpoint',
    api: 'API',
    webhook: 'Webhook',
    add: 'Add',
    array: 'Array',
    field: 'Field',
    type: 'Type',
    label: 'Label',
    test: 'Test',
    authentication: 'Authentication',
    authenticate: 'Authenticate',
    responseValues: 'Response values',
    noResponseValues:
      'No values received, please add your test request values in the API call and click test',
    afterSuccessfulResponse:
      'Once you receive a successful response the mappable values will appear here',
    noEndpoint: 'No endpoints added',
    finishSetup: 'Finish setup',
    connect: 'Connect',
    connectDescription: 'Add authentication method and connect',
    addEndpointDescription: 'Add endpoints for your intended functions',
    configure: 'Configure & test',
    configureDescription: 'Add test data, test and receive mappable values',
    endpointCurrentlyUsed: 'This endpoint is currently being used in a journey',
    removeEndpoint: 'Remove endpoint',
    areYouSureDelete:
      'Are you sure you want to remove this endpoint? All your current configurations and response values will be lost\n\nThis can not be undone but you can always add it back later',
    noJourneys: 'No journeys',
    noJourneysDescription: 'This integration is currently not used in any journeys',
    emptyFieldHandlingLabel: 'Empty field handling',
    emptyFieldHandlingDescription:
      'Configure how empty fields are sent to this integration in your requests',
    emptyFieldHandlingEmptyString: 'Send empty string as empty ("") *Default',
    emptyFieldHandlingNull: 'Send empty strings as null',
    emptyFieldHandlingSkip: 'Skip empty fields in requests',
    integrationSaved: 'Integration saved',
  },
};
